import { runInAction } from 'mobx';
import presizeBackendRest from '../../services/presize-backend-rest';
import { mapRestGroupsToStoreGroups } from '../../lib/adapters/article-group-adapter';
import { mapArticleToRestFormat } from './create-order-and-groups-action';
import { SizingStore } from './sizing-store';
import { SizingGroup } from '../../models/sizing-group';

export class RegroupAndReloadAction {
  async regroupAndReloadSizing(this: SizingStore): Promise<void> {
    const groupsResponse = await presizeBackendRest.createArticleGroups(this.referenceId, {
      articles: this.articles.map(mapArticleToRestFormat),
    });
    runInAction(() => {
      this.sizingGroups = mapRestGroupsToStoreGroups(groupsResponse.groups, this.groupingCgLevel, this.articles);
      // Propagate the order-level enableBox to all groups — the REST article-groups
      // response does not include enable_box, so SizingGroup defaults it to false.
      this.sizingGroups.forEach((group) => (group.enableBox = this.enableBox));
      this.resetGroupByArticleRefId();
      this.resetGroupById();
      this.sortGroupsByGroupNames();
    });
    await this.loadSizingRest();
  }

  /**
   * Regenerate groups from POST /article-groups WITHOUT re-fetching sizing.
   *
   * Used by the review flow (PT-57317): is_reviewed is part of the group key, so
   * toggling it forces a regroup to obtain the new group keys. The sizing itself is
   * unchanged, so instead of loadSizingRest() the existing group state is kept.
   *
   * A review toggle never merges or splits groups (two distinct groups always differ by
   * more than is_reviewed), so each regenerated group maps 1:1 to an existing store group
   * by its articles' reference IDs. The existing SizingGroup instances are updated IN PLACE
   * (only the group key + reviewed flag change) rather than being replaced, so that:
   *   - sizing/box state is preserved without a re-fetch, and
   *   - references held by callers (e.g. the box-restriction / enable-box flows that call
   *     markAsNotReviewed and keep mutating the group afterwards) stay valid.
   */
  async regroupPreservingSizing(this: SizingStore): Promise<void> {
    // is_reviewed is part of the group key and mapArticleToRestFormat derives it from
    // Article.reviewed, which is not reliably populated for groups loaded from the backend.
    // group.isReviewed is the source of truth, so sync every article's reviewed flag from its
    // group before building the request — otherwise unrelated already-reviewed groups would be
    // sent as is_reviewed=false and regrouped incorrectly.
    runInAction(() => {
      this.sizingGroups.forEach((group) => {
        group.articles.forEach((article) => (article.reviewed = group.isReviewed));
      });
    });

    const groupsResponse = await presizeBackendRest.createArticleGroups(this.referenceId, {
      articles: this.articles.map(mapArticleToRestFormat),
    });
    runInAction(() => {
      const previousGroupByArticleRefId = new Map<string, SizingGroup>();
      this.sizingGroups.forEach((group) => {
        group.articles.forEach((article) => previousGroupByArticleRefId.set(article.referenceId, group));
      });

      this.sizingGroups = groupsResponse.groups.map((restGroup) => {
        const referenceId = restGroup.articles.map((article) => article.reference_id).find(Boolean);
        const existingGroup = referenceId ? previousGroupByArticleRefId.get(referenceId) : undefined;
        if (existingGroup) {
          // Only the group key and reviewed flag change on a review toggle — update them in
          // place and keep everything else (sizing, box restriction, articles) untouched.
          existingGroup.groupKey = restGroup.key;
          existingGroup.id = restGroup.key;
          existingGroup.isReviewed = restGroup.is_reviewed;
          return existingGroup;
        }
        // Fallback if membership unexpectedly changed: use the freshly mapped group.
        return mapRestGroupsToStoreGroups([restGroup], this.groupingCgLevel, this.articles)[0];
      });

      this.resetGroupByArticleRefId();
      this.resetGroupById();
      this.sortGroupsByGroupNames();
    });
  }
}
